import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useReorder } from '../../api/hooks';
import type { RetailerOrderSummary } from '../../api/types';
import { useSession } from '../../auth/session';
import { invoiceFileName } from './order-list-view';

/**
 * R-06 — the Actions cell: download the order's CONSIGNMENT INVOICE(s), and reorder.
 *
 * What this button hands the retailer is the DESPATCH document: the pieces placed with them on
 * consignment, title still Miraki's (SH-06), nothing on it payable but the courier charge (SH-13).
 * It is NOT the purchase invoice they pay — that one is reached from R-22 Payables. The button is
 * labelled "Consignment invoice" (`shop.orders.invoiceDownload`) and never the bare word
 * "Invoice": an unqualified label here is how a retailer was once shown USD 12,900 payable for
 * goods they do not own.
 *
 * The document is fetched through the API (cookie session, scoped to this account there) and
 * handed to the browser as a file. Reorder adds the order's pieces to the cart — never submits
 * anything — and says what it left out.
 */
export function OrderActions({ order }: { order: RetailerOrderSummary }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const reorder = useReorder();
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const canOrder = me?.kind === 'retailer' && me.permissions.includes('shop.order');

  const fetchInvoice = async (id: string, reference: string) => {
    setBusy(id);
    setNote(null);
    try {
      const { blob, fileName } = await api.download(`/shop/consignment-invoices/${id}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? invoiceFileName(reference);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // After the click has handed the blob to the download, not before.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      setNote(t('shop.orders.invoiceFailed'));
    } finally {
      setBusy(null);
    }
  };

  const doReorder = () => {
    setNote(null);
    reorder.mutate(order.id, {
      onSuccess: (r) => {
        const parts: string[] = [];
        parts.push(r.added > 0
          ? t('shop.orders.reorderDone', { count: r.pieces, reference: order.reference })
          : t('shop.orders.reorderNothing', { reference: order.reference }));
        if (r.skipped.length > 0 && r.added > 0) {
          parts.push(t('shop.orders.reorderSkipped', { count: r.skipped.length, codes: r.skipped.join(', ') }));
        }
        setNote(parts.join(' '));
      },
      onError: () => setNote(t('shop.orders.reorderFailed')),
    });
  };

  return (
    <div className="mk-order-actions">
      {/*
        D97 — all four strings in this cell name the DESPATCH document explicitly, and the
        aria-label is the reason that mattered. It read "Download invoice {{reference}}" while
        only the visible button label had been qualified — and a screen-reader user hears ONLY
        the aria-label, so for them the qualification did not exist. They got the bare "Invoice"
        that caused the USD 12,900 defect: two documents whose names did not distinguish them,
        read by the wrong reader.

        Qualified on 31-Oct-2026 in `packages/i18n/src/en.ts` by the integrator, since this
        stream does not own that file. If a fifth string is ever added here, it names the
        document too — a label, a tooltip and an error are one control, and a reader who meets
        only one of them must still know which invoice it is.
      */}
      {order.invoices.length === 0 ? (
        <span className="mk-dim" title={t('shop.orders.invoiceNone')}>—</span>
      ) : order.invoices.map((i) => (
        <button
          key={i.id}
          type="button"
          className="mk-btn-link"
          disabled={busy === i.id}
          aria-label={t('shop.orders.invoiceDownloadFor', { reference: i.reference })}
          onClick={() => void fetchInvoice(i.id, i.reference)}
        >
          {order.invoices.length > 1 ? i.reference : t('shop.orders.invoiceDownload')}
        </button>
      ))}
      {canOrder ? (
        <button
          type="button"
          className="mk-btn-link"
          disabled={reorder.isPending}
          aria-label={t('shop.orders.reorderFor', { reference: order.reference })}
          onClick={doReorder}
        >
          {reorder.isPending ? t('shop.orders.reordering') : t('shop.orders.reorder')}
        </button>
      ) : null}
      {note ? (
        <span role="status" className="mk-dim">
          {note}{' '}
          {reorder.isSuccess && reorder.data.added > 0
            ? <Link to="/shop/order/review">{t('shop.orders.viewCart')}</Link> : null}
        </span>
      ) : null}
    </div>
  );
}
